'use client';

import React, { useState } from 'react';
import { Mail, Phone, Building2, CheckCircle2, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  orgName: string;
  email: string;
  contact: string;
}

export default function InterestRegistrationForm() {
  const [form, setForm] = useState<FormData>({ orgName: '', email: '', contact: '' });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!form.orgName.trim()) newErrors.orgName = 'Organisation name is required';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) newErrors.email = 'Valid email is required';
    if (!form.contact.trim() || !/^[0-9+\-\s]{7,15}$/.test(form.contact)) newErrors.contact = 'Valid contact number is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const supabase = createClient();
      await supabase.from('interest_registrations').insert({
        org_name: form.orgName.trim(),
        email: form.email.trim(),
        contact: form.contact.trim(),
      });
      setSubmitted(true);
    } catch {
      // Even if DB insert fails, show acknowledgement
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-4 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
          <CheckCircle2 size={36} className="text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Thank You for Your Interest!</h3>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
          We have received your registration. We will get back to you shortly to discuss conducting the BLS program at your place.
        </p>
        <div className="mt-6 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 max-w-xs w-full">
          <p className="text-xs text-muted-foreground">
            For immediate queries, reach us at{' '}
            <a href="mailto:iapcoimbatore@gmail.com" className="text-primary font-semibold underline underline-offset-2">
              iapcoimbatore@gmail.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 animate-slide-up">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Register Your Interest</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Interested in conducting the BLS program at your school or organisation? Let us know and we'll get in touch.
        </p>
      </div>

      {/* Organisation Name */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          School / Organisation Name
        </label>
        <div className="relative">
          <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={form.orgName}
            onChange={(e) => setForm({ ...form, orgName: e.target.value })}
            placeholder="e.g. St. Joseph's School, Coimbatore"
            className={`w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.orgName ? 'border-red-400' : 'border-border'
            }`}
          />
        </div>
        {errors.orgName && <p className="text-xs text-red-500 mt-1">{errors.orgName}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Email Address
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="principal@school.edu.in"
            className={`w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.email ? 'border-red-400' : 'border-border'
            }`}
          />
        </div>
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
      </div>

      {/* Contact Number */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Contact Number
        </label>
        <div className="relative">
          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="tel"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="+91 98765 43210"
            className={`w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.contact ? 'border-red-400' : 'border-border'
            }`}
          />
        </div>
        {errors.contact && <p className="text-xs text-red-500 mt-1">{errors.contact}</p>}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Submitting...
          </>
        ) : (
          'Register Interest'
        )}
      </button>

      <p className="text-xs text-muted-foreground text-center pt-1">
        We respect your privacy. Your details will only be used to contact you about this program.
      </p>
    </form>
  );
}
